import { Suspense, useState } from 'react';
import { createPortal } from 'react-dom';

import { useAddContractContext } from '@/contexts/add-contract-context';

import { ChannelDetails } from './channel-details';
import { ChannelSearchSection } from './channel-search-section';
import { ContractFormFooter } from './contract-form-footer';
import { Success } from './success';
import type { ContractSubmitSuccess } from './types';
import { useContractForm } from './use-contract-form';
import { Separator } from '@/components/ui/separator';

export type { ContractSubmitSuccess };

interface ChannelContentProps {
  submitSuccess?: ContractSubmitSuccess | null;
  onSubmitSuccess: (result: ContractSubmitSuccess) => void;
  onClose: () => void;
}

export function ChannelContent({
  submitSuccess = null,
  onSubmitSuccess,
  onClose,
}: ChannelContentProps) {
  const { channelId, channelName, setSelectedChannel } =
    useAddContractContext();
  // Portal only the footer so the search stays mounted above credential Suspense,
  // while the actions remain outside the shared scroll area.
  const [footerContainer, setFooterContainer] = useState<HTMLDivElement | null>(
    null,
  );

  if (submitSuccess) {
    return (
      <ContractSuccessBody submitSuccess={submitSuccess} onClose={onClose} />
    );
  }

  return (
    <>
      <ContractScrollArea>
        <ChannelSearchSection
          channelId={channelId}
          channelName={channelName}
          onChannelChange={setSelectedChannel}
        />
        {channelId != null && (
          <>
            <Separator className="max-w-[500px]" />
            <Suspense
              fallback={
                <ContractFormLoadingBody
                  footerContainer={footerContainer}
                  onClose={onClose}
                />
              }
            >
              <ContractFormBody
                key={channelId}
                channelId={channelId}
                footerContainer={footerContainer}
                onSubmitSuccess={onSubmitSuccess}
                onClose={onClose}
              />
            </Suspense>
          </>
        )}
      </ContractScrollArea>
      <div ref={setFooterContainer} className="shrink-0">
        {channelId == null && (
          <ContractFormFooter variant="disabled" onClose={onClose} />
        )}
      </div>
    </>
  );
}

function ContractScrollArea({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-0 flex-1 flex-col items-center gap-4 self-stretch overflow-y-auto p-4">
      {children}
    </div>
  );
}

function ContractSuccessBody({
  submitSuccess,
  onClose,
}: {
  submitSuccess: ContractSubmitSuccess;
  onClose: () => void;
}) {
  return (
    <>
      <ContractScrollArea>
        <Success
          name={submitSuccess.name}
          logoSquareUrl={submitSuccess.logoSquareUrl}
          logoRectangleUrl={submitSuccess.logoRectangleUrl}
        />
      </ContractScrollArea>

      <ContractFormFooter variant="success" onClose={onClose} />
    </>
  );
}

function ContractFormLoadingBody({
  onClose,
  footerContainer,
}: {
  onClose: () => void;
  footerContainer: HTMLDivElement | null;
}) {
  return (
    <>
      <ChannelDetails.Skeleton />
      {footerContainer &&
        createPortal(
          <ContractFormFooter variant="disabled" onClose={onClose} />,
          footerContainer,
        )}
    </>
  );
}

function ContractFormBody({
  channelId,
  footerContainer,
  onSubmitSuccess,
  onClose,
}: {
  channelId: number;
  footerContainer: HTMLDivElement | null;
  onSubmitSuccess: (result: ContractSubmitSuccess) => void;
  onClose: () => void;
}) {
  const { hookForm, channelDetails, handleFormSubmit, isSubmitting } =
    useContractForm({ channelId, onSubmitSuccess });

  return (
    <>
      <form
        id="add-contract-form"
        noValidate
        className="self-stretch"
        onSubmit={e => void handleFormSubmit(e)}
      >
        <ChannelDetails
          channelId={channelId}
          channelDetails={channelDetails}
          hookForm={hookForm}
        />
      </form>

      {footerContainer &&
        createPortal(
          <ContractFormFooter
            variant="submit"
            onClose={onClose}
            isSubmitting={isSubmitting}
            hookForm={hookForm}
          />,
          footerContainer,
        )}
    </>
  );
}
